/**
 * MemoListDrawer - メモ・カード・読書ノートの一覧を表示する左ドロワー
 *
 * 「メモ / カード / 読書ノート」のタブで分け、検索欄でタイトルと本文の先頭から絞り込む。
 * メモの切り替え・新規作成・削除を行う。メモ本文エディタ（NotionEditor）には依存しない独立コンポーネント。
 */

import { BookOpen, FileText, Network, Plus, Search, StickyNote, Trash2, X } from 'lucide-react'
import { useState } from 'react'

import type { BookNoteListItem } from '@/api/services/book-note'
import type { MemoListItem } from '@/api/services/memo'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { confirmDialog } from '@/lib/confirm-dialog'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

type Tab = 'memo' | 'card' | 'book'

type MemoListDrawerProps = {
  /** ドロワーの開閉状態 */
  open: boolean
  /** 開閉状態変化通知 */
  onOpenChange: (open: boolean) => void
  /** 表示するメモ一覧 */
  memos: MemoListItem[]
  /** 現在開いているメモID */
  activeId: number | null
  /** メモ選択時 */
  onSelect: (memoId: number) => void
  /** 新規作成時 */
  onCreate: () => void
  onCreateCard: () => void
  onOpenGraph: () => void
  /** 削除時 */
  onDelete: (memoId: number) => void
  /** 本文のある読書ノート（更新日時の降順） */
  bookNotes: BookNoteListItem[]
  onSelectBookNote: (productId: number) => void
}

const normalize = (text: string) => text.normalize('NFKC').toLowerCase()

/** スペース区切りの語をすべて含むか */
const matches = (words: string[], ...fields: string[]) => {
  const haystack = normalize(fields.join(' '))
  return words.every((w) => haystack.includes(w))
}

const byUpdatedDesc = <T extends { updated_at: string }>(a: T, b: T) =>
  parseUtcDate(b.updated_at).getTime() - parseUtcDate(a.updated_at).getTime()

export const MemoListDrawer = ({
  open,
  onOpenChange,
  memos,
  activeId,
  onSelect,
  onCreate,
  onCreateCard,
  onOpenGraph,
  onDelete,
  bookNotes,
  onSelectBookNote,
}: MemoListDrawerProps) => {
  // 未選択のうちは、開いているメモがカードならカードのタブを出す
  const [selectedTab, setSelectedTab] = useState<Tab | null>(null)
  const [query, setQuery] = useState('')
  const activeIsCard = memos.find((m) => m.id === activeId)?.is_card ?? false
  const tab: Tab = selectedTab ?? (activeIsCard ? 'card' : 'memo')

  const words = normalize(query).split(/\s+/).filter(Boolean)
  const sortedMemos = [...memos].sort(byUpdatedDesc).filter((m) => matches(words, m.title, m.preview))
  const lists = {
    memo: sortedMemos.filter((m) => !m.is_card),
    card: sortedMemos.filter((m) => m.is_card),
    book: bookNotes.filter((n) => matches(words, n.title, n.preview)),
  }
  const tabs: { value: Tab; label: string; icon: typeof FileText; count: number }[] = [
    { value: 'memo', label: 'メモ', icon: FileText, count: lists.memo.length },
    { value: 'card', label: 'カード', icon: StickyNote, count: lists.card.length },
    { value: 'book', label: '読書ノート', icon: BookOpen, count: lists.book.length },
  ]

  const handleDelete = async (e: React.MouseEvent, memo: MemoListItem) => {
    e.stopPropagation()
    const kind = memo.is_card ? 'カード' : 'メモ'
    if (!(await confirmDialog({ title: `この${kind}を削除しますか？`, destructive: true }))) return
    onDelete(memo.id)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* ヘッダー(56px=top-14)の下からドロワーを表示。開閉アニメはボタンと揃えて 300ms */}
      <SheetContent
        side="left"
        hideClose
        overlayClassName="top-14!"
        className="top-14! bottom-0 h-auto! w-72 max-w-[80vw] gap-0 p-0 duration-300! data-[state=closed]:duration-300! data-[state=open]:duration-300! sm:max-w-xs"
      >
        {/* アクセシビリティ用に視覚的に隠したタイトル（Radix要件） */}
        <SheetTitle className="sr-only">メモ一覧</SheetTitle>

        <div className="space-y-2 border-b p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="タイトル・本文で検索"
              className="h-8 w-full rounded-md border bg-transparent pr-7 pl-7 text-sm outline-none focus:border-primary"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="検索をクリア"
                className="absolute top-1/2 right-1.5 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
          <div className="flex gap-1" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={tab === t.value}
                onClick={() => setSelectedTab(t.value)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-md px-1 py-1 text-xs transition-colors',
                  tab === t.value
                    ? 'bg-accent font-medium text-foreground'
                    : 'text-muted-foreground hover:bg-accent/60',
                )}
              >
                <t.icon className={cn('size-3.5 shrink-0', t.value === 'card' && 'text-amber-500')} />
                {t.label}
                <span className="text-[10px] text-muted-foreground">{t.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-1">
          {tab !== 'book' && (
            <ul>
              {lists[tab].map((memo) => (
                <li
                  key={memo.id}
                  onClick={() => onSelect(memo.id)}
                  className={`group flex cursor-pointer items-center justify-between gap-1 rounded px-2 py-1 text-sm transition-colors ${
                    memo.id === activeId ? 'bg-accent' : 'hover:bg-accent/60'
                  }`}
                >
                  <p className="min-w-0 flex-1 truncate">
                    {memo.title || (memo.is_card ? '無題のカード' : '無題のメモ')}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, memo)}
                    className="rounded p-1 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                    aria-label="削除"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </li>
              ))}
              {lists[tab].length === 0 && (
                <li className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {query
                    ? '一致するものがありません'
                    : tab === 'card'
                      ? 'カードはまだありません'
                      : 'メモはまだありません'}
                </li>
              )}
              {!query && (
                <li>
                  <button
                    type="button"
                    onClick={tab === 'card' ? onCreateCard : onCreate}
                    className="flex w-full items-center gap-1 rounded px-2 py-1 text-sm text-muted-foreground transition hover:bg-accent active:opacity-70"
                  >
                    <Plus className="size-3.5" />
                    {tab === 'card' ? '新規カード' : '新規メモ'}
                  </button>
                </li>
              )}
            </ul>
          )}

          {tab === 'book' && (
            <ul>
              {lists.book.map((note) => (
                <li key={note.product_id}>
                  <button
                    type="button"
                    onClick={() => onSelectBookNote(note.product_id)}
                    title={note.preview}
                    className="flex w-full items-center rounded px-2 py-1 text-left text-sm transition-colors hover:bg-accent/60"
                  >
                    <span className="min-w-0 flex-1 truncate">{note.title}</span>
                  </button>
                </li>
              ))}
              {lists.book.length === 0 && (
                <li className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {query ? '一致するものがありません' : '読書ノートは作品の Info から作れます'}
                </li>
              )}
            </ul>
          )}
        </div>

        <div className="border-t p-1">
          <button
            type="button"
            onClick={onOpenGraph}
            className="flex w-full items-center gap-1 rounded px-2 py-1.5 text-sm text-muted-foreground transition hover:bg-accent active:opacity-70"
          >
            <Network className="size-3.5" />
            つながりのグラフ
          </button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
